import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Portal } from 'react-portal';
import { Form, Formik } from 'formik';

import { DATA_TABLE_SOURCE_UTILITY_ACTIVATION_FILES } from 'common/dataTableConstants';

import {
  SR_API_ACTION_UTILITY_ACTIVATION_FILE_CREATE,
  SR_API_ACTION_UTILITY_ACTIVATION_FILE_DELETE,
} from 'api/utilityActionTypes';

import {
  UTILITY_ACTIVATION_FILE_SOURCE_SPECTRUM_SCRAPER,
  UTILITY_ACTIVATION_FILE_SOURCE_COX_SCRAPER,
  UTILITY_ACTIVATION_FILE_SOURCE_USER_UPLOAD,
} from 'common/utility/constants';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrCard from 'client/components/SrCard/SrCard';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrBox from 'client/components/SrBox';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrS3FileDisplay from 'client/components/SrS3FileDisplay';

import './UPActivationFiles.less';

const UPActivationFiles = ({
  title = 'Upload Files',
  utilityActivationId,
  utilityActivationItemId,
}) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onSubmit = ({ fileUrls, reloadItems, resetForm }) => {
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_FILE_CREATE,
      payload: {
        utilityActivationId,
        source: UTILITY_ACTIVATION_FILE_SOURCE_USER_UPLOAD,
        urls: fileUrls,
      },
      onSuccess: (newFileUrls) => {
        resetForm();
        reloadItems({
          count: 1,
          filter: {
            'utilityActivationFiles.id': newFileUrls.map((file) => file.id),
          },
        });
      },
    });
  };

  const onDeleteFile = ({ fileId, reloadItems }) => {
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_FILE_DELETE,
      payload: {
        id: fileId,
      },
      onSuccess: () => {
        reloadItems({
          count: 1,
          filter: {
            'utilityActivationFiles.id': fileId,
          },
        });
      },
    });
  };

  const getFileUploadAuthorName = (file) => {
    if (
      file.source === UTILITY_ACTIVATION_FILE_SOURCE_SPECTRUM_SCRAPER ||
      file.source === UTILITY_ACTIVATION_FILE_SOURCE_COX_SCRAPER
    ) {
      return 'Assistant';
    }

    return file.authorName || 'Unknown';
  };

  return (
    <SrDataTableConnected
      itemsPropName="files"
      dataSource={DATA_TABLE_SOURCE_UTILITY_ACTIVATION_FILES}
      initialViewSettings={{
        sort: {
          createdAt: 'desc',
        },
        filter: {
          'utilityActivationFiles.utilityActivationId': utilityActivationId,
          'utilityActivationFiles.deletedAt': null,
        },
      }}
    >
      {({ files, reloadItems, dataTableApiState, onInfiniteScrollWrapperRef }) => (
        <>
          <SrCard padding="16px" borderRadius="14px" styleName="UPActivationFiles">
            <SrText font="greycliff" fontSize="18px" weight="800" display="block">
              {title}
            </SrText>
            <SrBox
              marginTop="4px"
              maxHeight="450px"
              overflowY="auto"
              overflowX="hidden"
              ref={onInfiniteScrollWrapperRef}
            >
              {files.map((file) => {
                const formattedDate = moment(file.createdAt).format('MMM D, YYYY [at] h:mma');
                const authorName = getFileUploadAuthorName(file);
                return (
                  <SrBox key={file.id} marginTop="10px">
                    <SrBox cursor="pointer" onClick={() => setIsModalOpen(true)}>
                      <SrS3FileDisplay url={file.url} maxHeight="280px" />
                    </SrBox>
                    <SrBox styleName="fileDetails">
                      <SrBox styleName="details">
                        <SrText font="greycliff" fontSize="12px" color="grey">
                          {authorName} | &nbsp;
                        </SrText>
                        <SrText font="greycliff" fontSize="12px" color="grey">
                          {formattedDate}
                        </SrText>
                      </SrBox>
                      <SrBox styleName="actions">
                        <SrText
                          font="greycliff"
                          fontSize="12px"
                          color="blue300"
                          cursor="pointer"
                          styleName="deleteFile"
                          onClick={() => onDeleteFile({ fileId: file.id, reloadItems })}
                        >
                          Delete File
                        </SrText>
                      </SrBox>
                    </SrBox>
                  </SrBox>
                );
              })}
            </SrBox>
            <SrBox>
              <Formik
                enableReinitialize
                initialValues={{
                  fileUrls: [],
                }}
                onSubmit={({ fileUrls }, { resetForm }) =>
                  onSubmit({ fileUrls, reloadItems, resetForm })
                }
                render={(formikBag) => (
                  <Form>
                    <SrFormikField
                      theme={SR_FIELD_THEME_ADOBE}
                      formikBag={formikBag}
                      formField={{
                        id: 'fileUrls',
                        type: 'file_multi',
                        properties: {
                          fileAccept: '*',
                          filePath:
                            `utility-activations/${utilityActivationId}` +
                            `/item-${utilityActivationItemId}/dealer-portal-confirmation-{uuid}`,
                          onChange: (fileUrls) => {
                            onSubmit({ fileUrls, reloadItems, resetForm: formikBag.resetForm });
                          },
                        },
                        validations: ['nonEmptyArray'],
                      }}
                    />
                  </Form>
                )}
              />
            </SrBox>
            {dataTableApiState.inProgress && (
              <SrFlex justify="center">
                <div className="loading-gif" />
              </SrFlex>
            )}
            {isModalOpen && (
              <Portal>
                <div
                  className="darkModal householdPhotosModal"
                  onClick={() => setIsModalOpen(false)}
                >
                  {files.map((file) => (
                    <div className="applicationPhotoContainer" key={file.id}>
                      <SrS3FileDisplay url={file.url} maxHeight="90vh" />
                    </div>
                  ))}
                </div>
              </Portal>
            )}
          </SrCard>
        </>
      )}
    </SrDataTableConnected>
  );
};

UPActivationFiles.propTypes = {
  utilityActivationId: PropTypes.number.isRequired,
};

UPActivationFiles.displayName = 'UPActivationFiles';

export default UPActivationFiles;
